import { useId } from "react";
import type { InputProps as RAInputProps } from "react-aria-components";
import { Input as RAInput, Label as RALabel } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import InputErrorMessage from "./InputErrorMessage";
import type { SlotClassNames } from "./types";

const input = tv({
  base: "space-y-0.5",
  variants: {
    variant: {
      bordered: { inputWrapper: "border border-border" },
      borderless: { inputWrapper: "" },
      flat: { inputWrapper: "px-0!" },
      searchbar: {
        inputWrapper: `
          rounded-lg border border-border bg-background/20! text-text-primary
        `,
        input: "[&::-webkit-search-cancel-button]:appearance-none",
      },
      filled: {
        labelRow: "mb-1.5",
        input: "text-base leading-6 placeholder:text-text-tertiary!",
      },
    },
    color: {
      background: { inputWrapper: "bg-background-2" },
      secondary: { inputWrapper: "bg-secondary" },
      tertiary: { inputWrapper: "bg-tertiary" },
      transparent: { inputWrapper: "bg-transparent" },
    },
    isInvalid: {
      true: { inputWrapper: "border-danger" },
    },
    size: {
      xs: { inputWrapper: "h-8 p-2 text-sm" },
      sm: { inputWrapper: "h-9 p-2 text-sm" },
      md: { inputWrapper: "h-12 p-4" },
      custom: { inputWrapper: "h-10 p-2" },
      // same height as `sm` but with the padding and icon gap pulled in, for
      // fields sitting inside an already-padded container
      tight: { inputWrapper: "h-9 gap-3 px-4 py-1 text-sm" },
    },
  },
  compoundVariants: [
    // surface and geometry live here so they win over the `color` and `size`
    // defaults, which would otherwise be merged in after the variant
    {
      variant: "filled",
      className: {
        inputWrapper: "h-10 gap-3 rounded-md bg-row-hover/50 px-3 py-2",
      },
    },
  ],
  defaultVariants: {
    variant: "borderless",
    color: "secondary",
    size: "md",
  },
  slots: {
    description: "mt-1! ps-1 text-xs text-text-secondary",
    labelRow:
      "data-label-end:flex data-label-end:items-center data-label-end:gap-1",
    label: "text-sm text-text-tertiary",
    startContent: "flex h-4 items-center",
    endContent: "flex h-4 items-center",
    inputWrapper: `
      flex flex-row items-center gap-4 rounded-lg text-text-secondary
      has-disabled:bg-transparent!
    `,
    input: `
      w-full bg-transparent text-sm leading-none transition-colors duration-100
      outline-none
      placeholder:text-text-secondary!
      focus-within:text-text-contrast
    `,
  },
});

export interface InputProps
  extends Omit<RAInputProps, "color" | "size">, VariantProps<typeof input> {
  readonly startContent?: React.ReactNode;
  readonly endContent?: React.ReactNode;
  readonly className?: string;
  readonly label?: string;
  /**
   * Rendered right after the label, outside the `<label>` element so
   * interactive content (e.g. a tooltip trigger) neither activates the input
   * nor leaks into its accessible name. Ignored without `label`.
   */
  readonly labelEnd?: React.ReactNode;
  readonly isRequired?: boolean;
  readonly errorMessage?: string;
  readonly description?: string;
  /** Per-slot overrides, matching the pattern `Switch` already exposes. */
  readonly classNames?: SlotClassNames<ReturnType<typeof input>>;
}

export default function Input({
  variant,
  color,
  startContent,
  endContent,
  className,
  ref,
  label,
  labelEnd,
  isRequired,
  isInvalid,
  errorMessage,
  description,
  size,
  classNames,
  ...props
}: InputProps & { readonly ref?: React.Ref<HTMLInputElement> }) {
  const generatedId = useId();
  const inputId = props.id ?? generatedId;
  const descriptionId = `${generatedId}-description`;
  const errorId = `${generatedId}-error`;
  const showError = Boolean(isInvalid && errorMessage);
  const describedBy =
    [
      props["aria-describedby"],
      description && descriptionId,
      showError && errorId,
    ]
      .filter(Boolean)
      .join(" ") || undefined;
  const {
    inputWrapper,
    base,
    input: _input,
    startContent: _startContent,
    endContent: _endContent,
    description: _description,
    label: _label,
    labelRow,
  } = input({
    variant,
    color,
    isInvalid,
    size,
  });

  return (
    <div
      className={base({
        className: `${className ?? ""} ${classNames?.base ?? ""}`.trim(),
      })}
    >
      {label && (
        <div
          data-label-end={labelEnd ? "" : undefined}
          className={labelRow({ className: classNames?.labelRow })}
        >
          <RALabel
            htmlFor={inputId}
            className={_label({ className: classNames?.label })}
          >
            {label}
            {isRequired && <span className="text-danger ml-0.5">*</span>}
          </RALabel>
          {labelEnd}
        </div>
      )}
      <div className={inputWrapper({ className: classNames?.inputWrapper })}>
        {startContent && (
          <div
            className={_startContent({ className: classNames?.startContent })}
          >
            {startContent}
          </div>
        )}
        <RAInput
          {...props}
          id={inputId}
          aria-invalid={isInvalid || props["aria-invalid"]}
          aria-required={isRequired || props["aria-required"]}
          aria-describedby={describedBy}
          ref={ref}
          className={_input({ className: classNames?.input })}
        />
        {endContent && (
          <div className={_endContent({ className: classNames?.endContent })}>
            {endContent}
          </div>
        )}
      </div>
      {description && (
        <div
          id={descriptionId}
          className={_description({ className: classNames?.description })}
        >
          {description}
        </div>
      )}
      {showError && (
        <InputErrorMessage id={errorId} className="mt-2">
          {errorMessage}
        </InputErrorMessage>
      )}
    </div>
  );
}
